/**
 * Başvuru sonrası isteğe bağlı kurum + rol (hq/Ali 2 Kas 2026).
 * Aynı başvuruyu günceller (POST /demo-talep/basvuru-tamamla); belirteç başvuru yanıtında gelir, 2 saat geçerli.
 * Form yolu yeni sayfaya geçtiği için belirteci sekmenin oturum belleğinde taşır; Google yolu doğrudan verir.
 * Kişi doldurmadan çıkarsa sorun yok: kurum ve rol kayit-tamamla'da da istenir.
 */
import { useRef, useState } from 'react';
import axios from 'axios';
import { ArrowPathIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { ROL_GRUPLARI } from './rolGruplari';

const BELIRTEC_ANAHTARI = 'riskmatik.basvuruTamamla';
const API_BASE = `${process.env.NEXT_PUBLIC_API_URL || 'https://api.riskmatik.com'}/api`;

export function basvuruBelirteciSakla(token: string): void {
  try { window.sessionStorage.setItem(BELIRTEC_ANAHTARI, token); } catch { /* oturum belleği kapalıysa kart görünmez */ }
}

export function basvuruBelirteciOku(): string | null {
  try { return window.sessionStorage.getItem(BELIRTEC_ANAHTARI); } catch { return null; }
}

function belirteciSil(): void {
  try { window.sessionStorage.removeItem(BELIRTEC_ANAHTARI); } catch { /* sessiz */ }
}

export default function BasvuruTamamlaKart({ token }: { token: string }) {
  const [kurum, setKurum] = useState('');
  const [rol, setRol] = useState('');
  const [kaydetBusy, setKaydetBusy] = useState(false);
  const kaydetInFlightRef = useRef(false);
  const [kaydedildi, setKaydedildi] = useState(false);
  const [hata, setHata] = useState<string | null>(null);

  const handleKaydet = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (kaydetInFlightRef.current) return;
    if (!kurum.trim() && !rol) { setHata('Kurum ya da rol girin.'); return; }
    kaydetInFlightRef.current = true;
    setKaydetBusy(true);
    setHata(null);
    try {
      await axios.post(`${API_BASE}/demo-talep/basvuru-tamamla`, { token, kurum: kurum.trim() || undefined, rol: rol || undefined });
      belirteciSil();
      setKaydedildi(true);
    } catch (err) {
      const mesaj = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setHata(mesaj || 'Bilgiler kaydedilemedi. Tekrar deneyin.');
    } finally {
      kaydetInFlightRef.current = false;
      setKaydetBusy(false);
    }
  };

  if (kaydedildi) {
    return (
      <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl flex items-start gap-3">
        <CheckCircleIcon className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-slate-700 leading-relaxed">Bilgileriniz başvurunuza eklendi. Teşekkürler.</p>
      </div>
    );
  }

  const inputCls = 'w-full min-h-[44px] px-3 py-2.5 text-base sm:text-sm text-slate-900 bg-white border border-slate-300 rounded-lg placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900 transition disabled:bg-slate-50';
  const labelCls = 'block text-[11px] font-bold text-slate-900 uppercase tracking-[0.12em] mb-1.5';

  return (
    <form onSubmit={handleKaydet} className="p-4 bg-white border border-slate-200 rounded-xl space-y-3">
      <div>
        <p className="text-[11px] font-bold text-slate-700 uppercase tracking-[0.12em]">İsteğe bağlı</p>
        <p className="text-sm text-slate-700 mt-1 leading-relaxed">Kurumunuzu ve rolünüzü yazarsanız başvurunuz daha hızlı onaylanır.</p>
      </div>
      {hata && <p role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{hata}</p>}
      <div>
        <label htmlFor="basvurutamamla-kurum" className={labelCls}>Kurum / OSGB Adı</label>
        <input id="basvurutamamla-kurum" type="text" value={kurum} maxLength={200} onChange={(e) => setKurum(e.target.value)}
          placeholder="Serbest çalışıyorsanız boş bırakın" className={inputCls} disabled={kaydetBusy} />
      </div>
      <div>
        <label htmlFor="basvurutamamla-rol" className={labelCls}>Rolünüz</label>
        <select id="basvurutamamla-rol" value={rol} onChange={(e) => setRol(e.target.value)} className={inputCls} disabled={kaydetBusy}>
          <option value="">Seçin</option>
          {ROL_GRUPLARI.map((grup) => (
            <optgroup key={grup.label} label={grup.label}>
              {grup.options.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </optgroup>
          ))}
        </select>
      </div>
      <button type="submit" disabled={kaydetBusy}
        className="w-full min-h-[44px] inline-flex items-center justify-center gap-2 px-4 py-3 bg-slate-900 text-white text-sm font-semibold rounded-lg hover:bg-slate-800 transition disabled:opacity-50 disabled:cursor-not-allowed">
        {kaydetBusy ? (<><ArrowPathIcon className="w-4 h-4 animate-spin" />Kaydediliyor</>) : 'Bilgileri ekle'}
      </button>
    </form>
  );
}
